import { useCallback, useContext } from 'react'

import { analytics } from 'legacy/utils/analytics'

import { merge } from '../utils/object'
import { join } from '../utils/string'
import { AnalyticsContext, type AnalyticsContextValue } from './AnalyticsContext'

export type TrackOptions = {
  /**
   * The `eventName` will not be joined into the parents prefixes.
   */
  noPrefix?: boolean

  /**
   * The `extraContext` will not be merged into the parents context.
   */
  noContext?: boolean
}

export function useAnalytics() {
  const value = useContext(AnalyticsContext)

  if (value == null) {
    throw new Error(`useAnalytics must be used within a AnalyticsProvider`)
  }

  const { prefix, context } = value

  /**
   * Track analytics event.
   */
  const track = useCallback(
    (
      eventName: string,
      extraContext?: AnalyticsContextValue['context'],
      options?: TrackOptions
    ) => {
      const joinedEventName = options?.noPrefix ? eventName : join([...prefix, eventName], ' / ')
      const mergedContext = options?.noContext ? extraContext : merge(context, extraContext)

      analytics.track(joinedEventName, mergedContext)
    },
    [prefix, context]
  )

  return {
    track,
  }
}
